import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Cadastro from "@/pages/Cadastro";
import Revisao from "@/pages/Revisao";
import RevisaoLegada from "@/pages/RevisaoLegada";
import Biblioteca from "@/pages/Biblioteca";
import Mapa from "@/pages/Mapa";
import Coleta from "@/pages/Coleta";
import Legado from "@/pages/Legado";
import Usuarios from "@/pages/Usuarios";
import Auditoria from "@/pages/Auditoria";
import Produtividade from "@/pages/Produtividade";
import ColetaPublica from "@/pages/ColetaPublica";
import Glossario from "@/pages/Glossario";
import PublicLayout from "@/components/PublicLayout";
import PublicLaunchGate from "@/components/PublicLaunchGate";
import GateRedirect from "@/components/GateRedirect";
import PublicHome from "@/pages/PublicHome";
import PublicMap from "@/pages/PublicMap";
import PublicObservatorio from "@/pages/PublicObservatorio";
import PublicSobre from "@/pages/PublicSobre";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster theme={theme === "dark" ? "dark" : "light"} position="top-right" richColors />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect Emergent OAuth session_id in URL fragment BEFORE routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* ÁREA PÚBLICA — protegida pelo Gate de Lançamento (quando habilitado) */}
      <Route element={<PublicLaunchGate />}>
        <Route element={<PublicLayout />}>
          <Route index element={<PublicHome />} />
          <Route path="/observatorio" element={<PublicObservatorio />} />
          <Route path="/mapa-publico" element={<PublicMap />} />
          <Route path="/sobre" element={<PublicSobre />} />
        </Route>
      </Route>

      <Route path="/login" element={<GateRedirect><Login /></GateRedirect>} />
      <Route path="/register" element={<GateRedirect><Register /></GateRedirect>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/painel" element={<Navigate to="/dashboard" replace />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/revisao" element={
          <ProtectedRoute roles={["admin", "coordenador", "revisor"]}>
            <Revisao />
          </ProtectedRoute>
        } />
        <Route path="/revisao-legada" element={
          <ProtectedRoute roles={["admin", "coordenador", "revisor"]}>
            <ErrorBoundary
              title="Não foi possível carregar a tela de Revisão da Base Legada."
              backHref="/revisao"
            >
              <RevisaoLegada />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/biblioteca" element={<Biblioteca />} />
        <Route path="/mapa" element={<Mapa />} />
        <Route path="/coleta" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <Coleta />
          </ProtectedRoute>
        } />
        <Route path="/coleta-publica" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <ColetaPublica />
          </ProtectedRoute>
        } />
        <Route path="/legado" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <ErrorBoundary
              title="Não foi possível carregar a tela de Importação da Base Legada."
              storageKeysToClear={["legado-simulation", "legado-filters"]}
              backHref="/biblioteca"
            >
              <Legado />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/usuarios" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <Usuarios />
          </ProtectedRoute>
        } />
        <Route path="/produtividade" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <Produtividade />
          </ProtectedRoute>
        } />
        <Route path="/auditoria" element={
          <ProtectedRoute roles={["admin", "coordenador"]}>
            <Auditoria />
          </ProtectedRoute>
        } />
        <Route path="/legendas" element={<Glossario />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <ThemedToaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
